import Link from '@/components/shared/Link';
import { useMediaQueryDeviceState } from '@/atoms/media-query-device';
import Popup from '@/components/shared/Popup';
import {
    useIsAppNavOpenedValue,
    useIsCatalogLandingPopupOpenedValue,
    useSetOpenedPopupsState,
} from '@/atoms/opened-popups';
import { Fragment, useRef, useMemo } from 'react';
import { m, useIsomorphicLayoutEffect } from 'framer-motion';
import { CATALOG_LANDING_POPUP_NAME } from '@/components/general/CatalogLandingPopup/CatalogLandingPopup';
import classNames from 'classnames';
import { FAVORITES_COOKIES_NAME, FAVORITES_POPUP_NAME } from '@/components/shared/FavoritesPopup/FavoritesPopup';
import { useCookies } from 'react-cookie';
import { useDebounce } from '@/hooks/use-debounce';
import Button from '@/components/shared/Button';
import SearchSVGWhite from '@/svg/search-white.svg?url';
import { FEEDBACK_POPUP_NAME } from '@/components/general/FeedbackPopup/FeedbackPopup';
import { useSetSelectedServiceValueFormState } from '@/atoms/form/selected-service-value-form-state';
import FavoritesSVG from '@/svg/favorites-empty.svg?url';
import CompareSVG from '@/svg/compare.svg?url';
import ViewedSVG from '@/svg/viewed.svg?url';
import PhoneSVG from '@/svg/phone.svg?url';
import debounce from 'lodash.debounce';
import { PHONE_BUTTON_POPUP_NAME } from '@/components/general/PhoneButton/PhoneButton';
import { COMPARE_COOKIES_NAME } from '@/components/shared/Card/CompareButton';
import { CATEGORIES_SLUGS } from '@/api/categories';
import { useIsClient } from '@/hooks/use-is-client';
import { tp } from '@/typograf';
import { useFiltersVisibleState } from '@/atoms/search-popup-visible';
import { SMALL_SEARCH_POPUP_NAME } from '../CatalogFilters/SmallSearchPopup';
import { useMediaQuery } from 'usehooks-ts';

const links = [
    {
        text: 'Каталог',
        popup: CATALOG_LANDING_POPUP_NAME,
        type: 'catalog',
        showOnDesktop: false,
    },
    {
        text: 'Главная',
        url: '/',
        type: 'link',
        showOnDesktop: true,
    },
    {
        text: 'О компании',
        url: '/about',
        type: 'link',
        showOnDesktop: true,
    },
    {
        text: 'Партнёрство и услуги',
        url: '/services',
        type: 'link',
        showOnDesktop: true,
    },
    {
        text: 'Статьи',
        url: '/blog',
        type: 'link',
        showOnDesktop: true,
    },

    {
        text: 'Контакты',
        url: '/contacts',
        type: 'link',
        showOnDesktop: true,
    },
    {
        text: 'Избранное',
        popup: FAVORITES_POPUP_NAME,
        type: 'popup',
        showOnDesktop: false,
        lineBefore: true,
    },
    {
        text: 'Сравнить товар',
        type: 'link',
        url: '/compare',
        showOnDesktop: false,
    },
    {
        text: 'Просмотренные товары',
        url: '/viewed',
        type: 'link',
        showOnDesktop: false,
    },
];

const variants = {
    hidden: {
        opacity: 0,
        y: -10,
        transition: { duration: 0.4 },
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5 },
    },
};

export const APP_NAV_POPUP_NAME = 'app-nav-popup';
const MotionLink = m(Link);

type ThemeName = 'black' | 'gray-brown';

const compareKeys = Object.keys(CATEGORIES_SLUGS).map((item) => `${COMPARE_COOKIES_NAME}-${item}`);

const Nav = () => {
    const { openPopup, closePopup, setOpenedPopups } = useSetOpenedPopupsState();
    const [device] = useMediaQueryDeviceState();
    const closeAppNavPopup = () => closePopup(APP_NAV_POPUP_NAME);
    const popupInner = useRef<HTMLDivElement>(null);
    const isAppNavPopupOpened = useIsAppNavOpenedValue();
    const isCatalogPopupOpened = useIsCatalogLandingPopupOpenedValue();
    const debouncedIsCatalogPopupOpened = useDebounce(isCatalogPopupOpened, 50);
    const closeCatalogPopup = () => closePopup(CATALOG_LANDING_POPUP_NAME);
    const [cookies] = useCookies([
        FAVORITES_COOKIES_NAME,
        ...Object.keys(CATEGORIES_SLUGS).map((item) => `${COMPARE_COOKIES_NAME}-${item}`),
    ]);
    const isClient = useIsClient();
    const burgerEl = useRef<HTMLButtonElement>(null);

    const setServiceValueFormState = useSetSelectedServiceValueFormState();
    const setFiltersVisible = useFiltersVisibleState();

    const compareLength = useMemo(() => {
        return compareKeys.reduce((sum, key) => {
            if (cookies[key]) {
                return sum + cookies[key].length;
            }
            return sum;
        }, 0);
    }, [cookies]);

    useIsomorphicLayoutEffect(() => {
        const onResize = () => {
            if (burgerEl.current && popupInner.current) {
                const rect = burgerEl.current.getBoundingClientRect();
                popupInner.current.style.top = `${Math.round(rect.bottom)}px`;
                popupInner.current.style.left = `${Math.round(rect.left)}px`;
            }
        };

        const debouncedOnResize = debounce(onResize, 100);
        window.addEventListener('resize', debouncedOnResize);
        onResize();
        document.addEventListener('new-page-ready', onResize);

        return () => {
            window.removeEventListener('resize', debouncedOnResize);
            document.removeEventListener('new-page-ready', onResize);
        };
    }, []);

    const isMobile = useMediaQuery('(max-width: 767px)');

    return (
        <nav
            className={classNames('app-nav', {
                'app-nav--catalog-opened': isCatalogPopupOpened,
                'app-nav--burger--opened': isAppNavPopupOpened,
            })}
        >
            <button
                type="button"
                className="app-nav-link app-nav-link--catalog"
                onClick={() => {
                    if (!isMobile) {
                        isCatalogPopupOpened ? closeCatalogPopup() : openPopup(CATALOG_LANDING_POPUP_NAME);
                    } else {
                        setOpenedPopups([APP_NAV_POPUP_NAME]);
                    }
                }}
            >
                <div
                    className={classNames('app-nav-link--catalog-text-wrapper text-xs', {
                        'is-opened': debouncedIsCatalogPopupOpened,
                    })}
                >
                    <div className="app-nav-link--catalog-text-wrapper__text">Каталог</div>
                </div>
                <div className="app-nav-catalog__icon" />
            </button>

            <button
                className={classNames('app-nav-burger', { 'is-opened': isAppNavPopupOpened })}
                onClick={() => {
                    if (isAppNavPopupOpened) {
                        closeAppNavPopup();
                    } else {
                        setOpenedPopups([APP_NAV_POPUP_NAME]);
                    }
                }}
                ref={burgerEl}
                aria-label="Меню"
            >
                <div className="app-nav-burger__text text-xs">Меню</div>
                <div className="app-nav-burger__icon">
                    <span />
                    <span />
                </div>
            </button>
            <div className="app-nav-links">
                <button
                    onClick={() => {
                        openPopup(FAVORITES_POPUP_NAME);
                    }}
                    className="app-nav-link__item"
                    aria-label="Открыть избранное"
                >
                    <div className="app-nav-link__item-icon">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={FavoritesSVG} className="img-fluid" alt="" decoding="async" />
                    </div>
                    <div className="app-nav-link__item-count">
                        {isClient && cookies.favorites && cookies.favorites.length > 0 && (
                            <span className="m-text-xs favorites-count">{`[ ${cookies.favorites.length} ]`}</span>
                        )}
                    </div>
                </button>
                <Link href={'/compare'} className="app-nav-link__item" aria-label="перейти к сравнению">
                    <div className="app-nav-link__item-icon">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={CompareSVG} className="img-fluid" alt="" decoding="async" />
                    </div>
                    <div className="app-nav-link__item-count">
                        {isClient && compareLength > 0 && (
                            <span className="m-text-xs favorites-count">{`[ ${compareLength} ]`}</span>
                        )}
                    </div>
                </Link>
                <Link href={'/viewed'} className="app-nav-link__item" aria-label="перейти к просматриваемым">
                    <div className="app-nav-link__item-icon">
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="viewed-icon"
                        >
                            <path
                                d="M2.72842 12.7466C2.02015 11.8264 1.66602 11.3663 1.66602 10.0002C1.66602 8.634 2.02015 8.17394 2.72842 7.25377C4.14265 5.41646 6.51444 3.3335 9.99935 3.3335C13.4843 3.3335 15.856 5.41646 17.2703 7.25377C17.9785 8.17394 18.3327 8.634 18.3327 10.0002C18.3327 11.3663 17.9785 11.8264 17.2703 12.7466C15.856 14.5838 13.4843 16.6668 9.99935 16.6668C6.51444 16.6668 4.14265 14.5838 2.72842 12.7466Z"
                                stroke="white"
                            />
                            <path
                                className="viewed-icon__inner"
                                d="M12.4993 10.0002C12.4993 11.3809 11.3801 12.5002 9.99935 12.5002C8.6186 12.5002 7.49935 11.3809 7.49935 10.0002C7.49935 8.61941 8.6186 7.50016 9.99935 7.50016C11.3801 7.50016 12.4993 8.61941 12.4993 10.0002Z"
                                stroke="white"
                            />
                        </svg>
                    </div>
                </Link>
            </div>

            <Popup className="app-nav-popup" name={APP_NAV_POPUP_NAME} overlay={true}>
                <m.div
                    variants={{
                        visible: {
                            opacity: 1,
                            transition: {
                                staggerChildren: 0.07,
                                duration: 0.5,
                                delay: 0.3,
                            },
                        },
                        hidden: {
                            opacity: 0,
                        },
                    }}
                    initial="hidden"
                    animate={isAppNavPopupOpened ? 'visible' : 'hidden'}
                    className="app-nav-popup-content"
                    ref={popupInner}
                >
                    <div className="burger-buttons">
                        <Button
                            icon={
                                // eslint-disable-next-line @next/next/no-img-element
                                <img
                                    src={PhoneSVG}
                                    className="img-fluid"
                                    alt=""
                                    width={17}
                                    height={16}
                                    decoding="async"
                                />
                            }
                            variant="primary"
                            iconReverse
                            onClick={() => {
                                setOpenedPopups([PHONE_BUTTON_POPUP_NAME]);
                            }}
                        >
                            Связаться
                        </Button>
                        <Button
                            variant="primary"
                            className="app-nav-burger-callback-button"
                            onClick={() => {
                                setServiceValueFormState('под заказ');
                                setOpenedPopups([FEEDBACK_POPUP_NAME]);
                            }}
                        >
                            {tp('Сервис «Под заказ»')}
                        </Button>
                    </div>
                    {device === 'desktop'
                        ? links.map((link, i) => {
                              switch (link.type) {
                                  case 'popup':
                                      return link.popup && link.showOnDesktop ? (
                                          <m.button
                                              variants={variants}
                                              type="button"
                                              className="app-nav-popup-link"
                                              key={i}
                                              onClick={() => {
                                                  closeAppNavPopup();
                                                  openPopup(link.popup);
                                              }}
                                              aria-label={link.text}
                                          >
                                              <span className="text-xs app-nav-popup-link__el">{link.text}</span>
                                              {isClient && link.popup === FAVORITES_POPUP_NAME && cookies.favorites && (
                                                  <span className="m-text-xs favorites-count">
                                                      {'[' + cookies.favorites.length + ']'}
                                                  </span>
                                              )}
                                          </m.button>
                                      ) : null;
                                  case 'link':
                                      return link.url && link.showOnDesktop ? (
                                          <MotionLink
                                              variants={variants}
                                              className="app-nav-popup-link"
                                              key={i}
                                              href={link.url}
                                              onClick={() => {
                                                  closeAppNavPopup();
                                              }}
                                          >
                                              <div className="text-xs app-nav-popup-link__el">{link.text}</div>
                                          </MotionLink>
                                      ) : null;
                                  default:
                                      return null;
                              }
                          })
                        : links.map((link, i) => {
                              switch (link.type) {
                                  case 'catalog':
                                      return (
                                          <m.button
                                              type="button"
                                              className="app-nav-popup-link"
                                              onClick={() => {
                                                  setOpenedPopups([CATALOG_LANDING_POPUP_NAME]);
                                              }}
                                              key={i}
                                          >
                                              <div className={classNames('h1', {})}>Каталог</div>
                                          </m.button>
                                      );
                                  case 'popup':
                                      return link.popup ? (
                                          <Fragment key={i}>
                                              {link.lineBefore && <div className="menu-line" />}
                                              <m.button
                                                  variants={variants}
                                                  type="button"
                                                  className="app-nav-popup-link"
                                                  onClick={() => {
                                                      closeAppNavPopup();
                                                      openPopup(link.popup);
                                                  }}
                                              >
                                                  <span className="text-xs app-nav-popup-link__el">{link.text}</span>
                                                  {isClient &&
                                                      link.popup === FAVORITES_POPUP_NAME &&
                                                      cookies.favorites &&
                                                      cookies.favorites.length > 0 && (
                                                          <span className="m-text-xs favorites-count">
                                                              {'[' + cookies.favorites.length + ']'}
                                                          </span>
                                                      )}
                                              </m.button>
                                          </Fragment>
                                      ) : null;
                                  case 'link':
                                      return link.url ? (
                                          <MotionLink
                                              variants={variants}
                                              className="app-nav-popup-link"
                                              key={i}
                                              href={link.url}
                                              onClick={() => {
                                                  closeAppNavPopup();
                                              }}
                                          >
                                              <div className="text-xs app-nav-popup-link__el">{link.text}</div>
                                              {isClient && link.url === '/compare' && compareLength > 0 && (
                                                  <span className="m-text-xs favorites-count">
                                                      {'[' + compareLength + ']'}
                                                  </span>
                                              )}
                                          </MotionLink>
                                      ) : null;
                                  default:
                                      return null;
                              }
                          })}
                </m.div>
            </Popup>

            <Button
                className="catalog-filter-btn app-nav__catalog-search-btn"
                icon={
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={SearchSVGWhite} className="img-fluid" alt="" decoding="async" />
                }
                size="sm"
                onClick={() => {
                    setFiltersVisible(false);
                    openPopup(SMALL_SEARCH_POPUP_NAME);
                }}
            >
                Поиск
            </Button>
        </nav>
    );
};

export default Nav;
